import React from 'react';
import { SparkIcon, TagColored, TooltipTrigger } from '@spark';
import { getSparkTagVariant } from '@/features/datasets/components/DataSelection/sparkLabelColors';
import './versionsDiff.css';

/** Per-label occurrence counts for one side of a record diff. */
export function payloadLabelCounts(payload) {
  const counts = {};
  if (!payload) return counts;
  if (Array.isArray(payload.entities) && payload.entities.length > 0) {
    for (const span of payload.entities) {
      counts[span.label] = (counts[span.label] || 0) + 1;
    }
    return counts;
  }
  for (const label of payload.labels || []) {
    counts[label] = (counts[label] || 0) + 1;
  }
  return counts;
}

/**
 * Tag descriptors for one changed record: plain (unchanged), changed
 * (`label (n→m)`), added (`+ label (n)`), removed (struck through).
 * `hideCounts` (single-label classification) drops the per-label count from
 * every tag, where it is always 1.
 */
export function buildAnnotationTags(record, { hideCounts = false } = {}) {
  const fromCounts = payloadLabelCounts(record.from);
  const toCounts = payloadLabelCounts(record.to);
  const tags = [];

  const sortedLabels = (counts) => Object.keys(counts).sort((a, b) => a.localeCompare(b));
  const tagText = (label, count) => (hideCounts ? label : `${label} (${count})`);

  if (record.status === 'added') {
    for (const label of sortedLabels(toCounts)) {
      tags.push({ label, kind: 'added', text: tagText(label, toCounts[label]) });
    }
    return tags;
  }
  if (record.status === 'removed') {
    for (const label of sortedLabels(fromCounts)) {
      tags.push({ label, kind: 'removed', text: tagText(label, fromCounts[label]) });
    }
    return tags;
  }

  const all = new Set([...Object.keys(fromCounts), ...Object.keys(toCounts)]);
  const plain = [];
  const changed = [];
  const added = [];
  const removed = [];
  for (const label of [...all].sort((a, b) => a.localeCompare(b))) {
    const fromCount = fromCounts[label] || 0;
    const toCount = toCounts[label] || 0;
    if (fromCount > 0 && toCount > 0) {
      if (fromCount === toCount) {
        plain.push({ label, kind: 'plain', text: tagText(label, toCount) });
      } else {
        changed.push({ label, kind: 'changed', text: `${label} (${fromCount}→${toCount})` });
      }
    } else if (toCount > 0) {
      added.push({ label, kind: 'added', text: tagText(label, toCount) });
    } else {
      removed.push({ label, kind: 'removed', text: tagText(label, fromCount) });
    }
  }
  return [...plain, ...changed, ...added, ...removed];
}

/** Tag descriptors for the record diff modal's "Changes Summary" row (only the changes). */
export function buildChangesSummaryTags(record, options = {}) {
  return buildAnnotationTags(record, options).filter((tag) => tag.kind !== 'plain');
}

/**
 * Single-label classification: `{from, to}` when a changed record switched
 * from one label to another (each version has exactly one label), else null.
 */
export function buildSingleLabelTransition(record) {
  if (record.status !== 'changed') return null;
  const fromLabels = Object.keys(payloadLabelCounts(record.from));
  const toLabels = Object.keys(payloadLabelCounts(record.to));
  if (fromLabels.length !== 1 || toLabels.length !== 1) return null;
  if (fromLabels[0] === toLabels[0]) return null;
  return { from: fromLabels[0], to: toLabels[0] };
}

/** `old → new` label transition (single-label classification records). */
export const LabelTransition = ({ transition, allLabels }) => (
  <span className="vdm-label-transition">
    <TagColored
      variant={getSparkTagVariant(transition.from, allLabels)}
      label={transition.from}
    />
    <SparkIcon slug="arrow-right" size="small" />
    <TagColored variant={getSparkTagVariant(transition.to, allLabels)} label={transition.to} />
  </span>
);

/**
 * Tag descriptors for one version's row in the Record Diff modal: this
 * version's per-label counts, underlined when the count differs from the other
 * version, with a `+` prefix on labels the other version lacks (`markNew`).
 */
export function buildVersionTags(payload, otherPayload, { markNew = false, hideCounts = false } = {}) {
  const counts = payloadLabelCounts(payload);
  const otherCounts = payloadLabelCounts(otherPayload);
  return Object.keys(counts)
    .sort((a, b) => a.localeCompare(b))
    .map((label) => {
      const isNew = !(label in otherCounts);
      return {
        label,
        kind: markNew && isNew ? 'added' : 'plain',
        highlight: counts[label] !== (otherCounts[label] || 0),
        text: hideCounts ? label : `${label} (${counts[label]})`,
      };
    });
}

export const AnnotationTag = ({ tag, allLabels }) => {
  const tagElement = (
    <TagColored
      variant={getSparkTagVariant(tag.label, allLabels)}
      label={tag.text}
      showPrefixIcon={tag.kind === 'added'}
      prefixIconSlug="plus"
      className={
        [
          tag.kind === 'removed' ? 'vdm-tag-removed' : '',
          tag.highlight ? 'vdm-tag-highlight' : '',
        ]
          .filter(Boolean)
          .join(' ') || undefined
      }
    />
  );
  if (tag.tooltip) {
    return (
      <TooltipTrigger text={tag.tooltip}>
        <span>{tagElement}</span>
      </TooltipTrigger>
    );
  }
  return tagElement;
};

/**
 * Row of annotation tags capped at `maxVisible`, with a "+N" overflow tag whose
 * tooltip lists the hidden labels.
 */
export const AnnotationTagRow = ({ tags, allLabels, maxVisible = 6 }) => {
  if (!tags.length) {
    return <span className="vdm-empty-cell">-</span>;
  }
  const visible = tags.slice(0, maxVisible);
  const hidden = tags.slice(maxVisible);
  return (
    <div className="vdm-tag-row">
      {visible.map((tag) => (
        <AnnotationTag key={`${tag.kind}-${tag.label}`} tag={tag} allLabels={allLabels} />
      ))}
      {hidden.length > 0 ? (
        <TooltipTrigger text={hidden.map((tag) => tag.text).join(', ')}>
          <span>
            <TagColored variant="disabled" label={`+${hidden.length}`} />
          </span>
        </TooltipTrigger>
      ) : null}
    </div>
  );
};
